import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { toCalendarDay } from '@adornis/datetime/calendarDay.js';
import type { AppliedContract } from '../entities/AppliedContract.js';
import type { Contract } from '../entities/Contract.js';
import type { ContractSupplement, ContractSupplementInnerSection } from '../entities/ContractSupplement.js';
import { type ContractWithSupplementRuntimes, isContractActive } from './contractTimeframes.js';
import { getActiveSectionsForPointInTime } from './getActiveSectionsForPointInTime.js';
import { getMinAndMaxDatesOfSupplement } from './getMinAndMaxDatesOfSupplement.js';

export type SupplementStatus =
  /** currently opened for editing, so the form shows this state even though it may not be in effect */
  | 'editing'
  /** the contract was terminated and forced an end date onto the supplement */
  | 'terminated'
  /** not signed yet, which beats every date based state because it is not legally in effect */
  | 'draft'
  /** every section that carries changes is in effect at the given day */
  | 'active'
  /** some but not all sections carrying changes are in effect */
  | 'partial'
  | 'future'
  | 'expired'
  /** no dates at all, so there is nothing to be active */
  | 'inactive';

export type ContractStatus = 'active' | 'unsigned' | 'future' | 'terminated' | 'expired';

export type SupplementStatusSupplement = NarrowedEntity<
  ContractSupplement,
  {
    signedAt: 1;
    startDateGeneral: 1;
    endDateGeneral: 1;
    startDateAG1: 1;
    endDateAG1: 1;
    startDateAG2: 1;
    endDateAG2: 1;
    automaticallyAssignedEndDateOfContract: 1;
    changes: { path: 1 };
  }
>;

export interface SupplementStatusInfo {
  status: SupplementStatus;
  /** sections in effect at the given day */
  activeSections: ContractSupplementInnerSection[];
  /** sections this supplement actually changes, a section without changes cannot be meaningfully active */
  changedSections: ContractSupplementInnerSection[];
  start: Maybe<Date>;
  end: Maybe<Date>;
}

const SECTION_PATH_MATCHERS: Record<ContractSupplementInnerSection, (path: string) => boolean> = {
  general: path => path.includes('agInfos') || path.includes('agSozialVersicherung'),
  ag1: path => path.includes('agEingruppierung1'),
  ag2: path => path.includes('agEingruppierung2'),
};

/** Which of the three inner sections this supplement touches at all. */
export const getChangedSections = (supplement: SupplementStatusSupplement): ContractSupplementInnerSection[] => {
  const paths = supplement.changes?.map(change => change.path) ?? [];

  return (Object.keys(SECTION_PATH_MATCHERS) as ContractSupplementInnerSection[]).filter(section =>
    paths.some(SECTION_PATH_MATCHERS[section]),
  );
};

/**
 * Determines how a supplement should be presented for a given point in time.
 *
 * The order of the checks is the priority of the states: a supplement being edited is shown as such, a
 * terminated one can never become active again, and an unsigned one is never presented as active because it is
 * not legally in effect yet, no matter what its dates say.
 */
export const getSupplementStatusForDate = (
  supplement: SupplementStatusSupplement,
  date: Date,
  options?: { isBeingEdited?: boolean; forcedActive?: boolean; forcedInactive?: boolean },
): SupplementStatusInfo => {
  const { start, end } = getMinAndMaxDatesOfSupplement(supplement);
  const changedSections = getChangedSections(supplement);
  const activeSections = getActiveSectionsForPointInTime(supplement, date);

  const base = { activeSections, changedSections, start, end };

  if (options?.isBeingEdited || options?.forcedActive) return { ...base, status: 'editing' };

  const terminatedAt = supplement.automaticallyAssignedEndDateOfContract;
  const day = toCalendarDay(date);
  if (terminatedAt && terminatedAt < day) return { ...base, status: 'terminated' };

  if (!supplement.signedAt) return { ...base, status: 'draft' };

  if (!options?.forcedInactive && activeSections.length) {
    // sections without changes carry no information, so full activity is measured against the changed ones
    const relevantSections = changedSections.length ? changedSections : activeSections;
    const allRelevantActive = relevantSections.every(section => activeSections.includes(section));
    return { ...base, status: allRelevantActive ? 'active' : 'partial' };
  }

  if (!start && !end) return { ...base, status: 'inactive' };
  if (start && start > day) return { ...base, status: 'future' };
  if (end && end < day) return { ...base, status: 'expired' };

  return { ...base, status: 'inactive' };
};

export type ContractStatusContract = NarrowedEntity<
  Contract | AppliedContract,
  {
    _id: 1;
    createdAt: 1;
    agInfos: { einstellungsdatum: 1; gekuendigtZum: 1; unterzeichnungsdatum: 1 };
  }
> &
  Partial<ContractWithSupplementRuntimes>;

/**
 * Status of a contract at the selected point in time. Unlike the supplement variant this is deliberately
 * evaluated against the date the user is looking at, not against today.
 */
export const getContractStatusForDate = (contract: ContractStatusContract, date: Date): ContractStatus => {
  if (isContractActive(date, contract)) return 'active';

  const day = toCalendarDay(date);
  const start = contract.agInfos?.einstellungsdatum;
  const end = contract.agInfos?.gekuendigtZum;

  if (end && end < day) return 'terminated';
  if (start && start > day) return 'future';
  if (!contract.agInfos?.unterzeichnungsdatum) return 'unsigned';

  return 'expired';
};
